import React, {useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {Box, Button, Chip, Divider, Stack, Typography} from "@mui/material";
import {toast} from "react-toastify";
import {useDaapSaveDistributorPlatformSubscriptions} from "../hooks/DaapSubscription.hooks.jsx";
import SubscriptionTable from "./SubscriptionTable.jsx";

const summaryCardSx = {
    p: 2, bgcolor: "#fff", borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const emptyDraft = {
    added: new Map(), removed: new Map(),
};

const reportTypeChipColorByValue = {
    weekly: "success",
    monthly: "primary",
    inventory: "warning",
};

export default function DaapDistPlatSubPrevEdit({
                                                    selectedCompany,
                                                    selectedContactEmails,
                                                    distributorPlatformDraft,
                                                    distributorPlatformDraftRows,
                                                    deleteSubscription,
                                                    isInitialDataLoading,
                                                    resetForm,
                                                }) {
    const [daapSaveDistributorPlatformSubscriptions, {loading: isSavingSubscriptions}] = useDaapSaveDistributorPlatformSubscriptions();

    const companyName = selectedCompany?.displayName || "N/A";
    const contactCount = selectedContactEmails.length;

    const hasChanges = distributorPlatformDraft.added.size > 0 || distributorPlatformDraft.removed.size > 0;

    const subscriptionColumns = useMemo(() => [{
        field: "reportType", headerName: "REPORT", flex: 0.8, minWidth: 140, renderCell: (params) => (<Chip
                label={params.value}
                size="small"
                variant="outlined"
                color={reportTypeChipColorByValue[params.row.reportType] || "default"}
            />),
    }, {
        field: "dataSource", headerName: "DATA SOURCE", flex: 1, minWidth: 160, renderCell: (params) => (<Chip
                label={params.value}
                size="small"
                variant="outlined"
                color={params.value === "KEHE" ? "secondary" : "info"}
            />),
    }, {
        field: "company", headerName: "COMPANY", flex: 1.4, minWidth: 240,
    }, {
        field: "email", headerName: "SAVED WITH (EMAIL)", flex: 1.2, minWidth: 220,
    },], []);

    const handleSaveSubscription = useCallback(async () => {
        try {
            const distributorPlatforms = distributorPlatformDraftRows.filter((row) => !row.isMarkedForDeletion).map(({
                email,
                reportType,
                dataSource,
                company,
            }) => ({
                email,
                reportType,
                dataSource,
                company,
            }))

            const removedDistributorPlatforms = Array.from(distributorPlatformDraft.removed.values()).map(({email, reportType, dataSource, company}) => ({
                email,
                reportType,
                dataSource,
                company,
            }));

            const response = await daapSaveDistributorPlatformSubscriptions({
                variables: {
                    distributorPlatform: distributorPlatforms,
                    deletedDistributorPlatform: removedDistributorPlatforms,
                },
            });

            const result = response?.data?.sdnoDaapUpdateDistributorPlatformSubscription;

            if (result?.success) {
                toast.success(result.message || "Subscriptions saved successfully.");
                resetForm?.();
                return;
            }

            toast.error(result?.message || "Failed to save subscriptions. Please try again.");
        } catch {
            toast.error("Failed to save subscriptions. Please try again.");
        }
    }, [daapSaveDistributorPlatformSubscriptions, distributorPlatformDraftRows, resetForm]);

    return (<Box marginTop={1}>
            <Stack spacing={3}>
                <Typography variant="h6" fontWeight="bold">
                    Preview & Edit Subscriptions
                </Typography>

                <Box sx={summaryCardSx}>
                    <Stack direction="row" spacing={4} sx={{mb: 1}}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                COMPANY / ORGANIZATION
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {companyName}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                SELECTED CONTACTS
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {contactCount} Contact(s)
                            </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{flexGrow: 1}}/>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{fontStyle: "italic"}}
                    >
                        Selections are saved with the contact&apos;s email address.
                    </Typography>
                </Box>
                <SubscriptionTable
                    title="Subscriptions"
                    rows={isSavingSubscriptions || isInitialDataLoading ? [] : distributorPlatformDraftRows}
                    columns={subscriptionColumns}
                    onDeleteRow={deleteSubscription}
                    loading={isSavingSubscriptions || isInitialDataLoading}
                />

                <Button
                    className="save-button"
                    variant="contained"
                    disabled={!hasChanges || isSavingSubscriptions}
                    onClick={handleSaveSubscription}
                >
                    {isSavingSubscriptions ? "SAVING" : "SAVE"} SUBSCRIPTIONS
                </Button>
            </Stack>
        </Box>);
}

DaapDistPlatSubPrevEdit.propTypes = {
    selectedCompany: PropTypes.shape({
        displayName: PropTypes.string,
    }), selectedContactEmails: PropTypes.arrayOf(PropTypes.string), distributorPlatformDraft: PropTypes.shape({
        added: PropTypes.instanceOf(Map), removed: PropTypes.instanceOf(Map),
    }), distributorPlatformDraftRows: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        reportTypeDisplayName: PropTypes.string,
        dataSource: PropTypes.string,
        company: PropTypes.string,
        reportType: PropTypes.string,
        isNew: PropTypes.bool,
    })), deleteSubscription: PropTypes.func, isInitialDataLoading: PropTypes.bool, resetForm: PropTypes.func,
};

DaapDistPlatSubPrevEdit.defaultProps = {
    selectedCompany: undefined,
    selectedContactEmails: [],
    distributorPlatformDraft: emptyDraft,
    distributorPlatformDraftRows: [],
    deleteSubscription: undefined,
    isInitialDataLoading: false,
    resetForm: undefined,
};
